import type { SelectedFilters } from '@/types/filter';
import { FILTER_GROUP } from '@/types/filter';

export const LABELS = {
    STRING: {
        CURRENCY: '#cy#'
    },
    NAME: {
        CURRENCY: 'CURRENCY',
        GAME_NAME: 'GAMENAME'
    }
};

export const REGIONS = {
    MI: 'MI',
    PA: 'PA',
    NJ: 'NJ',
    WV: 'WV',
    CT: 'CT',
    MR: 'MR',
    ON: 'ON',
    AB: 'AB',
    EXTERNAL: 'EXTERNAL'
} as const;

export const REGIONS_SHOW_RESPONSIBLE_GAMING: readonly (keyof typeof REGIONS)[] = [
    REGIONS.MI,
    REGIONS.NJ,
    REGIONS.WV,
    REGIONS.ON,
    REGIONS.AB,
    REGIONS.PA
];

export const BRAND = {
    FANDUEL: 'fanduel',
    MOHEGANSUN: 'mohegansun'
} as const;

export enum PRODUCT_THEME {
    DEFAULT = 'default',
    CHRISTMAS = 'christmas',
    NFL = 'nfl'
}

export const COUNTRY = {
    USA: 'usa',
    CANADA: 'canada'
} as const;

export const COUNTRY_ABBREVIATION = {
    USA: 'us',
    CANADA: 'ca'
} as const;

export const MH_RESERVATION_STATE = 'MR';

export const MH_RESERVATION_POSTAL_CODE = '06382';

export const BRAND_REGION_MAPPING = {
    [COUNTRY.USA]: {
        [BRAND.FANDUEL]: {
            defaultRegion: REGIONS.MI,
            regions: [REGIONS.MI, REGIONS.PA, REGIONS.NJ, REGIONS.WV, REGIONS.ON, REGIONS.AB]
        },
        [BRAND.MOHEGANSUN]: {
            defaultRegion: REGIONS.CT,
            regions: [REGIONS.CT, REGIONS.MR]
        }
    },
    [COUNTRY.CANADA]: {
        [BRAND.FANDUEL]: {
            defaultRegion: REGIONS.ON,
            regions: [REGIONS.AB, REGIONS.ON]
        },
        [BRAND.MOHEGANSUN]: {
            defaultRegion: REGIONS.ON,
            regions: [REGIONS.AB, REGIONS.ON]
        }
    }
};

export const LOYALTY_CODE_OPERATION = {
    ADD: 'add',
    READ: 'read'
};

export const LOYALTY_ERROR_MESSAGES = {
    CONDITIONAL_CHECK_FAILED_EXCEPTION: 'ConditionalCheckFailedException'
};

export const HEADERS = {
    BASE_DOMAIN: 'x-base-domain',
    REGION_CODE: 'x-region-code',
    BRAND: 'x-brand',
    LOYALTY_CODE: 'x-loyalty-code',
    CACHE_CONTROL: 'Cache-Control',
    AUTHORIZATION: 'Authorization',
    CDN_REGION: 'cloudfront-viewer-country-region',
    CDN_POSTAL_CODE: 'cloudfront-viewer-postal-code',
    IS_DEVICE_DESKTOP_HEADER: 'cloudfront-is-desktop-viewer',
    IS_DEVICE_MOBILE_HEADER: 'cloudfront-is-mobile-viewer',
    IS_DEVICE_TABLET_HEADER: 'cloudfront-is-tablet-viewer',
    GAN_API_TOKEN: 'gan-api-token',
    GAN_API_PLAYER: 'gan-api-player',
    X_APPLICATION: 'x-application',
    X_ORIGINAL_FORWARDED_FOR: 'x-original-forwarded-for',
    X_FORWARDED_FOR: 'x-forwarded-for',
    X_PX_AUTHORIZATION: 'x-px-authorization',
    AUTH_TOKEN: 'X-Auth-Token',
    LOGIN_TOKEN: 'X-Login-Token',
    REQUEST_ID: 'x-uuid'
};

export const ROUTES = {
    HOMEPAGE: '/',
    SHOW_HIDDEN_REGIONS: '/letMeIntoAllRegions',
    HEALTH: '/health',
    PREVIEW: '/preview',
    GAME: '/game/',
    CATEGORY: '/c/',
    CATEGORY_HUB: '/c/hub/',
    STATIC_PAGE: '/',
    DYNAMIC_PAGE: '/',
    SITE_WIDE_JACKPOT_RULES: '/site-wide-jackpot-rules',
    REWARDS: '/rewards',
    REWARD_MACHINE_INFO: '/reward-machine-info',
    REWARD_MACHINE_EXIT: '/reward-machine-exit-screen',
    BONUS_SPINS_DETAILS: '/bonus-spins-details',
    MOMENTUM: '/momentum',
    INFLUENCER_HUB: '/influencer-hub/',
    GAMEPLAY_SESSION: '/gameplay-session/',
    LIVESTREAM: '/livestream'
};

export const SUBDOMAINS = {
    RAF: 'refer.casino.'
} as const;

export const UNIFIED_MODULES_ROUTES = {
    LOGIN: '/login',
    REGISTER: '/join',
    DEPOSIT: '/account/deposit',
    WITHDRAWALS: '/withdrawals'
};

export const SESSION_KEYS = {
    FAVOURITE_GAMES: 'favouriteGame',
    RECENTLY_PLAYED_GAME: 'recentlyPlayedGame',
    TRIGGER_LOYALTY_FLOW: 'triggerLoyaltyFlow',
    EXCLUSIVE_SHOWN: 'exclusiveShown',
    SCROLL_TO: 'scrollTo'
};

export const LOCALES = {
    MI: 'fd-mi',
    PA: 'fd-pa',
    NJ: 'fd-nj',
    WV: 'fd-wv',
    ON: 'fd-on',
    AB: 'fd-ab',
    CT: 'ms-ct',
    MR: 'ms-mr'
} as const;

export const PRODUCT = {
    CASINO: 'casino',
    XSELL: 'xsell',
    SPORTSBOOK: 'sportsbook',
    POKER: 'poker'
} as const;

export const DEVICE = {
    DESKTOP: 'desktop',
    MOBILE: 'mobile',
    MOBILE_WEB: 'mobile-web',
    TABLET: 'tablet'
} as const;

export const APP_PLATFORM = {
    Web: 'Web',
    Android: 'Android',
    Xsell: 'Xsell',
    Ios: 'Ios'
};

export const ORIENTATIONS = {
    LANDSCAPE: 'landscape',
    PORTRAIT: 'portrait'
} as const;

export const SLICE_TYPE = {
    CREATIVE_ITEM: {
        EXTERNAL_LANDING_PAGE: 'external_lp',
        GAME_CATEGORY: 'game___category',
        PROMOTION: 'promotion'
    },
    JACKPOT_ZONE: 'jackpot_zone',
    JACKPOT_ZONE_ENTRY_POINT: 'jackpot_zone_entry_point',
    JACKPOT_HERO_ZONE: 'jackpot_hero',
    SITE_WIDE_JACKPOT_HERO_ZONE: 'site-wide-jackpot-hero-zone',
    COLLECTION: 'collection',
    GRID_COLLECTION: 'grid_collection',
    RECENTLY_PLAYED_GAMES: 'recently_played_games',
    TRENDING_GAMES: 'trending_games_configuration',
    GAMES_RECOMMENDER: 'games_recommender_configuration',
    ANIMATED_TRAILER: 'animated_trailer',
    EXPLORE_NEW_GAMES: 'explore_new_games_hero_zone',
    EXCLUSIVE_PRE_LAUNCH: 'pre_launch',
    EXCLUSIVE_POST_LAUNCH: 'post_launch',
    CAROUSEL: 'carousel',
    ANIMATED_CAROUSEL: 'animated_carousel',
    ONGOING_PROMOTIONS: 'ongoing_promotions',
    RICH_TEXT_ZONE: 'rich_text',
    RECOMMENDED_GAMES: 'recommended_games',
    BECAUSE_YOU_PLAYED: 'because_you_played',
    CATEGORY_GAMES_PLACEHOLDER: 'category_games_placeholder',
    FAVOURITE_GAMES: 'favourite_games',
    VIDEO_PREVIEW: 'video_preview',
    EVOLUTION_CATEGORY: 'evolution_category',
    SEARCH_FIELD: 'search_field',
    INCENTIVE_GAMES_ENTRY_POINT_ZONE: 'incentive_games_entry_point_zone',
    COLLECTIONS_ENTRY_SHOWCASE: 'collections_entry_showcase',
    LEGACY_CAROUSEL: 'legacy_carousel',
    ACTION_LINKS: 'action_links',
    SITE_WIDE_JACKPOT: 'jackpot',
    SITE_WIDE_JACKPOT_PAGE: 'Site Wide Jackpot',
    DAILY_JACKPOT_PAGE: 'Daily Jackpots',
    DAILY_JACKPOT_ZONE: 'jackpot_zone_entry_point',
    EXPLORE_VIDEOS: 'explore_videos_zone',
    SITE_WIDE_JACKPOT_MULTIPLIERS: 'jackpot_multiplier_boost',
    GAME_WINDOW_PARAMS: 'game_window_params'
};

export const PAGE_TYPES = {
    SITE_WIDE_JACKPOT: 'Site Wide Jackpot',
    NEW: 'New',
    DEFAULT: 'Default'
};

//TODO: Add the rest of environments when they are supported
export const ENVIRONMENT = {
    localAwsDevelopment: 'local-aws-development',
    dev2: 'dev2',
    mockHarness: 'mock-harness'
};

export const TIMERS_IN_MS = {
    MINUS_ONE_SECOND: -1_000,
    ONE_SECOND: 1_000,
    ONE_MINUTE: 60_000,
    ONE_HOUR: 3_600_000,
    ONE_DAY: 86_400_000,
    ONE_WEEK: 604_800_000,
    ONE_MONTH: 2_592_000_000,
    ONE_YEAR: 365 * 86_400_000
};

export const INITIAL_SELECTED_FILTERS: SelectedFilters = {
    [FILTER_GROUP.THEME]: [],
    [FILTER_GROUP.FEATURE]: [],
    [FILTER_GROUP.GENRE]: [],
    [FILTER_GROUP.STUDIO]: []
};

export const STUDIO_FILTERS = [
    { title: 'IGT', uids: ['igt', 'igt-isoft'] },
    { title: 'Evolution', uids: ['evolution', 'evolution-live', 'red-tiger', 'netent', 'big-time-gaming'] },
    { title: 'Games Global', uids: ['dgc'] },
    {
        title: 'Light & Wonder',
        uids: ['lw', 'fantasma', 'reel-play', 'lightning-box', 'big-time-gaming-via-lw', 'playzido', 'lw-rgs']
    },
    { title: 'Everi', uids: ['everi'] },
    { title: 'White Hat', uids: ['white-hat-gaming'] },
    { title: 'Aristocrat', uids: ['aristocrat', 'aristocrat-direct'] },
    { title: 'AGS', uids: ['ags'] }
];

export const ZONES = {
    TRENDING_GAMES: 'trending-games-zone',
    TOP_PICKS_FOR_YOU: 'top-picks-for-you-zone',
    RECENTLY_PLAYED_GAMES: 'gaming-recently-played-zone',
    ANIMATED_TRAILER: 'animated-trailer-zone',
    EXCLUSIVE_LAUNCH: 'exclusive_launch_zone',
    FAVORITES_GAMES: 'favorite-games-zone',
    EXPLORE_NEW_GAMES: 'explore-new-games-hero-zone',
    EXPLORE_VIDEOS: 'explore-videos-zone',
    NEW_TO_YOU: 'new-to-you-zone'
};

export const JACKPOTS_ZONE_NAME = 'jackpots' as const;

/** Zone names used for Amplitude tracking and data attributes in exclusive launch (pre/post) contexts. */
export const EXCLUSIVE_ZONE_NAMES = {
    POST_LAUNCH: 'post_launch',
    PRE_LAUNCH: 'pre_launch',
    POST_LAUNCH_EARLY_ACCESS: 'post_launch_early_access',
    PRE_LAUNCH_EARLY_ACCESS: 'pre_launch_early_access'
} as const;

export const EXCLUSIVE_LAUNCH_TYPE = {
    EARLY_ACCESS: 'early_access'
} as const;

export const HERO_TYPES = {
    DOUBLE_YOUR_BET: 'Double Your Bet',
    DAILY_JACKPOTS: 'Daily Jackpots'
} as const;

export const LOCAL_STORAGE = {
    EXCLUSIVE_MODAL_SEEN: 'exclusiveModalsSeen',
    MODAL_COMPLETED: 'modalCompleted',
    EXCLUSIVE_MODAL_SEEN_THIS_SESSION: 'exclusiveModalsSeenThisSession',
    EXCLUSIVE_AUTOMATED_POPUP: 'exclusiveAutomatedPopup',
    EXCLUSIVE_MANUAL_POPUP: 'exclusiveManualPopup',
    USER_HAS_LOGGED_OUT: 'userHasLoggedOut',
    JUST_LOGGED_IN: 'justLoggedIn',
    HAS_SEEN_NEW_REWARDS_PAGE: 'hasSeenNewRewardsPage',
    DYB_INTERSTITIAL_SHOWN: 'dyb-interstitial-shown'
};

export const VIDEO_ERROR_BY_CONTEXT = {
    PRE_LAUNCH: 'EXCLUSIVE_PRELAUNCH_VIDEO_ERROR',
    POST_LAUNCH: 'EXCLUSIVE_POSTLAUNCH_VIDEO_ERROR',
    MODAL: 'EXCLUSIVE_MODAL_VIDEO_ERROR',
    ANIMATED_TRAILER: 'ANIMATED_TRAILER_VIDEO_ERROR'
};

export const EXCLUSIVE_LAUNCH_ZONE_DEFAULTS = {
    ANNOUNCEMENT_TEXT: 'Coming soon',
    VIEW_TRAILER_TEXT: 'View trailer',
    PLAY_NOW_BUTTON: 'Play now'
};

export const FEATURES = {
    CATEGORY_V2_LAYOUT: 'has_category_v2'
} as const;

export const FOOTER_BUTTONS = {
    COOKIE_CONSENT: 'COOKIE_CONSENT',
    SWITCH_REGION: '#switch-region'
} as const;

export const SEARCH = {
    MIN_QUERY_LENGTH: 3,
    PAGE_SIZE_DESKTOP: 10,
    PAGE_SIZE_MOBILE: 20
} as const;

// CAS-59841: temporarily disabled PokerStars entry
// export const POKERSTARS_URL = 'https://poker.fanduel.com/';
